import { Children, isValidElement, useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

function isFooterChild(child: ReactNode): boolean {
  if (!isValidElement<{ className?: string }>(child)) return false;
  return typeof child.props.className === 'string' && /\bcn-footer\b/.test(child.props.className);
}

function splitModalChildren(children: ReactNode) {
  const body: ReactNode[] = [];
  let footer: ReactNode = null;
  Children.forEach(children, (child) => {
    if (isFooterChild(child)) footer = child;
    else body.push(child);
  });
  return { body, footer };
}

export function Modal({
  open,
  title,
  children,
  onClose,
  boxClassName,
  showCloseButton = true,
  overlayZIndex,
  portal = false,
}: {
  open: boolean;
  title?: string;
  children: ReactNode;
  onClose: () => void;
  boxClassName?: string;
  showCloseButton?: boolean;
  /** Overrides default overlay z-index (1000). Use 1100 for confirm dialogs above modal dropdowns. */
  overlayZIndex?: number;
  /** Portals overlay to document.body so nested modals stack above parent modal content. */
  portal?: boolean;
}) {
  const { t } = useTranslation();
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const { body, footer } = splitModalChildren(children);
  const showHead = Boolean(title) || showCloseButton;

  const modal = (
    <div
      className="overlay on"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      style={overlayZIndex != null ? { zIndex: overlayZIndex } : undefined}
      onMouseDown={(e) => (e.target === e.currentTarget ? onClose() : null)}
    >
      <div
        className={['cn-mbox app-modal', boxClassName].filter(Boolean).join(' ')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {showHead ? (
          <div className="app-modal__head">
            {title ? (
              <div className="app-modal__title" id={titleId}>
                {title}
              </div>
            ) : (
              <span aria-hidden="true" />
            )}
            {showCloseButton ? (
              <button type="button" className="app-modal__close cn-x" aria-label={t('common.close')} onClick={onClose}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="app-modal__body cn-body">{body}</div>
        {footer}
      </div>
    </div>
  );

  return portal ? createPortal(modal, document.body) : modal;
}
